<script>
  import Layout from '../components/Layout.svelte';
  import { t } from '../i18n/index.js';
  import { themeId, colorMode, inwdDetails } from '../stores/theme.js';
  import { THEMES, COLOR_MODES, getTheme } from '../themes/index.js';
  import { Palette, Sun, Moon, Check } from '../lib/icons/index.js';

  /**
   * Appearance panel — picks the theme, the colour mode and, for a theme that
   * offers them, the illustrated details.
   *
   * The choice is an account preference: `stores/preferences.js` saves it to
   * the account and brings it back on the next sign-in, on any device, and a
   * copy cached in this browser paints the page before anything has loaded.
   * It never affects invoice data, and the copy says so, because "tema" in an
   * invoicing tool is otherwise easy to mistake for something that changes
   * what gets sent to SdI.
   *
   * Each theme is previewed with its own literal palette rather than with CSS
   * variables: custom properties only resolve for the *active* theme, so a
   * catalogue that draws all of them at once has to carry its own swatches.
   * Those live in the registry (`src/themes/index.js`), one palette per colour
   * mode, so the miniature shows what the viewer would actually get.
   */

  // When `embedded` is true the panel renders only its content, so the
  // Settings page can slot it in as a tab body. Mirrors the other panels.
  export let embedded = false;

  const MODE_ICONS = { dark: Moon, light: Sun };

  // The theme's optional details, if it has any (a registry capability, not a
  // theme id): the switch appears only for a theme that offers them, and the
  // stored choice survives a switch to a theme that does not.
  $: themeDetails = getTheme($themeId).details ?? null;
</script>

{#snippet panel()}
  {#if !embedded}
    <div class="page-header">
      <div class="flex items-center gap-2">
        <Palette size={22} class="text-action" />
        <h1 class="section-title mb-0">{$t('appearance.title')}</h1>
      </div>
    </div>
  {/if}

  <p class="text-muted text-sm mb-6 max-w-3xl">{$t('appearance.description')}</p>

  <!-- Theme ------------------------------------------------------------- -->
  <fieldset class="mb-8 border-0 p-0 m-0">
    <legend class="label mb-1">{$t('appearance.themeSectionTitle')}</legend>
    <p class="text-muted text-xs mb-4 max-w-3xl">{$t('appearance.themeSectionHint')}</p>

    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {#each THEMES as theme}
        {@const preview = theme.preview[$colorMode]}
        {@const shape = theme.preview}
        {@const selected = $themeId === theme.id}
        <label
          class="card focus-ring-host p-4 cursor-pointer block transition-all"
          style={selected ? 'border-color: var(--color-primary); box-shadow: var(--card-shadow-hover);' : ''}>
          <input
            type="radio"
            name="appearance-theme"
            class="sr-only"
            value={theme.id}
            checked={selected}
            on:change={() => themeId.select(theme.id)} />

          <!-- Miniature of the theme, drawn with the theme's own values. -->
          <div
            class="mb-4 p-3 flex items-center gap-3 overflow-hidden"
            aria-hidden="true"
            style="background-color: {preview.background}; border-radius: {shape.radius};">
            <div
              class="flex-1 px-3 py-2"
              style="background-color: {preview.surface}; border-radius: {shape.radius};">
              <span
                class="block leading-none text-xl"
                style="color: {preview.text}; font-family: {shape.headingFont};">Aa</span>
              <span
                class="block mt-1 text-[10px] leading-none"
                style="color: {preview.text}; font-family: {shape.bodyFont};">0123 · € 1.240,00</span>
            </div>
            <div class="flex flex-col gap-1.5 shrink-0">
              <span class="block h-5 w-5" style="background-color: {preview.primary}; border-radius: {shape.radius};"></span>
              <span class="block h-5 w-5" style="background-color: {preview.secondary}; border-radius: {shape.radius};"></span>
            </div>
          </div>

          <div class="flex items-start justify-between gap-2">
            <div>
              <span class="block text-sm font-bold text-heading">{$t(theme.labelKey)}</span>
              <span class="block text-xs text-muted">{$t(theme.taglineKey)}</span>
            </div>
            {#if selected}
              <span class="badge badge-cyan shrink-0">
                <Check size={12} />
                {$t('appearance.inUse')}
              </span>
            {/if}
          </div>

          <p class="text-xs text-muted mt-3">{$t(theme.descriptionKey)}</p>
        </label>
      {/each}
    </div>
  </fieldset>

  <!-- Colour mode -------------------------------------------------------- -->
  <fieldset class="mb-8 border-0 p-0 m-0">
    <legend class="label mb-1">{$t('appearance.modeSectionTitle')}</legend>
    <p class="text-muted text-xs mb-4 max-w-3xl">{$t('appearance.modeSectionHint')}</p>

    <div class="flex flex-wrap gap-3">
      {#each COLOR_MODES as mode}
        {@const Icon = MODE_ICONS[mode]}
        {@const selected = $colorMode === mode}
        <label class="btn focus-ring-host {selected ? 'btn-primary' : 'btn-secondary'} cursor-pointer gap-2">
          <input
            type="radio"
            name="appearance-mode"
            class="sr-only"
            value={mode}
            checked={selected}
            on:change={() => colorMode.set(mode)} />
          <Icon size={16} />
          {$t(`appearance.modes.${mode}`)}
        </label>
      {/each}
    </div>
  </fieldset>

  {#if themeDetails}
    <!-- Dettagli inWD: the switch sits beside its name, with a swatch of
         what it adds, because it is the one choice here a theme card does
         not show. -->
    <section class="details-setting mb-8 measure-wide" aria-labelledby="details-title">
      <div class="details-setting-head">
        <h2 id="details-title" class="label mb-0">{$t(themeDetails.labelKey)}</h2>
        <button type="button" role="switch" class="switch" aria-checked={$inwdDetails ? 'true' : 'false'}
          aria-labelledby="details-title" aria-describedby="details-description details-dark-note details-state"
          on:click={() => inwdDetails.toggle()}>
          <span class="switch-track" aria-hidden="true"><span class="switch-thumb"></span></span>
        </button>
        <span id="details-state" class="text-sm text-muted" role="status" aria-live="polite">
          {$inwdDetails ? $t('appearance.details.on') : $t('appearance.details.off')}
        </span>
      </div>
      {#if themeDetails.preview}
        {@const light = getTheme($themeId).preview.light}
        <!-- The frame and chips are drawn on paper and exist only in light mode,
             so the swatch is always the light-mode rail — its ground and ink from
             the registry palette — and says so in dark mode. -->
        <figure class="details-preview">
          <span class="details-preview-ground" aria-hidden="true" style="background-color: {light.surface};">
            <span class="details-preview-item"
              style="background-image: url('{themeDetails.preview.image}'); background-size: {themeDetails.preview.size}; background-position: {themeDetails.preview.position}; color: {light.primary};"
            >{$t('nav.dashboard')}</span>
          </span>
          {#if $colorMode === 'dark'}
            <figcaption class="text-xs text-muted mt-1">{$t('appearance.details.previewLight')}</figcaption>
          {/if}
        </figure>
      {/if}
      <p id="details-description" class="text-sm text-muted">{$t(themeDetails.descriptionKey)}</p>
      <p id="details-dark-note" class="text-xs text-muted mt-1">{$t('appearance.details.darkNote')}</p>
    </section>
  {/if}

  <details class="help-disclosure">
    <summary>{$t('appearance.storageNoticeTitle')}</summary>
    <p class="text-sm max-w-3xl">{$t('appearance.storageNotice')}</p>
  </details>
{/snippet}

{#if embedded}
  {@render panel()}
{:else}
  <Layout>
    {@render panel()}
  </Layout>
{/if}
